/**
 * Streams a line that arrives whole - a Chat reply, the learner's typed or transcribed question - word by word
 * (owner 18 Oct: "It should literally be showing kind of streamed responses"). Sol, verbatim: "After the complete
 * guardrail-approved Chat reply arrives, reveal it at 6 words/second on phone and web. A tap/click anywhere on the
 * revealing reply immediately shows the rest. Voice transcription remains genuinely segment-driven."
 */
import {useEffect, useReducer, useRef} from 'react';

export const REVEAL_WORDS_PER_SECOND = 6;

/** Words in a piece of text (runs of non-space). */
export const countWords = text => (String(text || '').match(/\S+/g) || []).length;

/** How many words are out `elapsedMs` after a reveal starts: the first at once, then one every 1/wps seconds. */
export const wordsRevealed = (elapsedMs, wordsPerSecond = REVEAL_WORDS_PER_SECOND) =>
  1 + Math.floor((Math.max(0, Number(elapsedMs) || 0) * wordsPerSecond) / 1000);

/** The text up to and including its `count`th word, its own spacing and line breaks kept. */
export function firstWords(text, count) {
  const source = String(text || '');
  if (count <= 0) return '';
  const words = /\S+/g;
  let seen = 0;
  let match;
  while ((match = words.exec(source))) {
    seen += 1;
    if (seen === count) return source.slice(0, match.index + match[0].length);
  }
  return source;
}

/** What a reveal shows `elapsedMs` after it started. */
export const revealedWords = (text, elapsedMs, wordsPerSecond = REVEAL_WORDS_PER_SECOND) =>
  firstWords(text, wordsRevealed(elapsedMs, wordsPerSecond));

/**
 * Reveals `text` at `wordsPerSecond` from the moment `key` changes. Text that grows while revealing keeps the pace;
 * text that grows after the reveal caught up (live speech, which is slower than the pace) continues from where it is,
 * so a transcript that arrives a segment at a time still shows as it comes. `revealAll()` shows the rest at once.
 * Inactive: the text as it is (voice-mode tutor subtitles).
 */
export function useWordReveal(text, {key = '', active = true, wordsPerSecond = REVEAL_WORDS_PER_SECOND} = {}) {
  const [, rerender] = useReducer(n => n + 1, 0);
  const total = countWords(text);
  const now = Date.now();
  const ref = useRef(null);
  let state = ref.current;
  if (!state || state.key !== key) {
    state = {key, base: 0, startAt: now, skipped: false, lastTotal: 0, shown: 0};
    ref.current = state;
  }
  if (total !== state.lastTotal) {
    const caughtUp = state.shown >= state.lastTotal;
    if (total < state.lastTotal) {
      // the line was wiped and restarted: reveal the new words from the start
      state.base = 0;
      state.startAt = now;
    } else if (caughtUp) {
      state.base = state.lastTotal;
      state.startAt = now;
    }
    state.lastTotal = total;
  }
  const shown =
    !active || state.skipped ? total : Math.min(total, state.base + wordsRevealed(now - state.startAt, wordsPerSecond));
  state.shown = shown;
  const revealing = shown < total;

  // The next word is due when the elapsed time crosses the next 1/wps step.
  const dueIn = revealing ? state.startAt + ((shown - state.base) * 1000) / wordsPerSecond - now : null;
  useEffect(() => {
    if (dueIn === null) return undefined;
    const timer = setTimeout(rerender, Math.max(16, dueIn));
    return () => clearTimeout(timer);
  }, [dueIn, shown, key]);

  const revealAll = () => {
    if (!ref.current || ref.current.skipped) return;
    ref.current.skipped = true;
    rerender();
  };

  return {text: revealing ? firstWords(text, shown) : String(text || ''), revealing, revealAll};
}
